/**
 * Mock Widget Data Generators (Story 5.5, Task 1.3)
 *
 * Realistic mock data for each widget type, used for
 * development/preview mode before Story 5.11 populates real data.
 * Includes edge cases: very large numbers, single data points,
 * empty arrays, long text content.
 */

import type { WidgetConfig } from "./templateUtils";
import type {
  KpiData,
  TimeSeriesData,
  CategoricalData,
  DistributionData,
  TabularData,
  TopPostRecord,
  TopPostsData,
  FunnelData,
  DemographicData,
  AnyWidgetPayload,
  WidgetData,
} from "./widgetData";
import { createDataEnvelope } from "./widgetData";
import { metricKeysFromConfig } from "./widgetCompatibility";

// ── KPI Mock Data ──────────────────────────────────────────────────

export function generateMockKpiData(config: Record<string, unknown>): KpiData {
  const format = (config.format as string) ?? "number";
  const comparisonEnabled = config.comparisonEnabled as boolean;

  const baseValues: Record<string, { value: number; previous: number; format: KpiData["format"] }> = {
    sessions: { value: 12345, previous: 11200, format: "number" },
    activeUsers: { value: 8432, previous: 7890, format: "number" },
    bounceRate: { value: 42.3, previous: 45.1, format: "percentage" },
    cost: { value: 1234567, previous: 1100000, format: "currency" },
    conversions: { value: 543, previous: 498, format: "number" },
    ctr: { value: 3.8, previous: 3.2, format: "percentage" },
  };

  const metricKey = config.metricKey as string;
  const base = baseValues[metricKey] ?? { value: 42567, previous: 38900, format: format as KpiData["format"] };

  return {
    value: base.value,
    previousValue: comparisonEnabled ? base.previous : undefined,
    format: base.format,
    sparkline: buildMockSparkline(base.previous, base.value, metricKey),
  };
}

/**
 * A deterministic ~10-point series easing from `previous` → `current` with a
 * small organic wiggle (seeded by the metric key so it's stable across renders
 * and tests — no Math.random). The last point is always the current value.
 */
function buildMockSparkline(previous: number, current: number, seedKey = ""): number[] {
  const N = 10;
  let seed = 0;
  for (let i = 0; i < seedKey.length; i++) seed = (seed * 31 + seedKey.charCodeAt(i)) % 1000;
  const amp = Math.abs(current - previous || current * 0.06) * 0.35;
  const out: number[] = [];
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const trend = previous + (current - previous) * t;
    // bounded sine wiggle (deterministic), tapering to 0 at the last point so the
    // series lands exactly on `current`.
    const wiggle = i === N - 1 ? 0 : Math.sin((i + seed) * 1.3) * amp * (1 - t);
    out.push(Math.max(0, Math.round((trend + wiggle) * 100) / 100));
  }
  out[N - 1] = current;
  return out;
}

// ── Deterministic seed for stable mock data across re-renders ─────

function seededValue(seed: number, index: number, range: number, offset: number): number {
  const hash = ((seed + index * 2654435761) >>> 0) % range;
  return hash + offset;
}

/** A well-mixed per-point hash (seed AND index avalanche together) — used only by
 *  the line/bar series wave's jitter, where each point must be independent. */
function mixHash(seed: number, index: number, range: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (index + 1), 2654435761) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519) >>> 0;
  h ^= h >>> 13;
  return (h >>> 0) % range;
}

/**
 * A smooth, organic-looking mock value for series `seed` at step `index`. Unlike a
 * pure additive hash (which made every metric move in the SAME direction each
 * step), the wave's FREQUENCY and PHASE are derived from the seed, so each metric
 * draws its own distinct shape — they rise and fall independently — while a touch
 * of seeded jitter keeps it from looking too perfectly sinusoidal. Deterministic.
 */
function seededSeriesValue(seed: number, index: number, amplitude: number, base: number): number {
  const phase = (seed % 1000) / 1000 * Math.PI * 2; // 0..2π, per series
  const freq = 0.45 + ((seed >>> 7) % 100) / 100 * 0.7; // ~0.45..1.15 rad/step
  const wave = Math.sin(index * freq + phase); // -1..1
  const jitter = (mixHash(seed, index, 100) / 100 - 0.5) * 0.35; // ±~0.17, independent
  const norm = (wave + jitter + 1) / 2; // ~0..1
  return Math.round(base + norm * amplitude);
}

/** Stable per-string seed (FNV-1a-ish) so each metric name yields a distinct,
 *  reproducible mock curve — different metrics never share the same series shape. */
function stringSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h >>> 0;
}

// ── Time Series Mock Data ──────────────────────────────────────────

export function generateMockTimeSeriesData(config: Record<string, unknown>): TimeSeriesData {
  // Coerce ANY metric-binding shape (string[] / object[] / metricKey) into keys;
  // fall back to a default series so an unbound chart still previews sample data.
  const keys = metricKeysFromConfig(config);
  const metrics = keys.length > 0 ? keys : ["sessions"];
  const comparisonEnabled = config.comparisonEnabled as boolean;
  const points: TimeSeriesData["points"] = [];

  for (let i = 0; i < 14; i++) {
    const date = new Date(2026, 1, i + 1);
    const point: { date: string; [metricKey: string]: number | string } = {
      date: date.toISOString().split("T")[0]!,
    };
    for (let m = 0; m < metrics.length; m++) {
      // Per-metric seed from the metric NAME drives a distinct wave shape (freq +
      // phase), so series rise/fall independently instead of moving in parallel.
      // A per-series base level (700 + m·450) also separates them vertically.
      const mSeed = stringSeed(metrics[m]!);
      point[metrics[m]!] = seededSeriesValue(mSeed, i, 900, 700 + m * 450);
    }
    points.push(point);
  }

  if (!comparisonEnabled) {
    return { points };
  }

  // Generate comparison data: ±20% variation from current values
  const comparisonPoints: TimeSeriesData["points"] = points.map((point, i) => {
    const compDate = new Date(2026, 0, i + 18); // Previous period dates
    const compPoint: { date: string; [metricKey: string]: number | string } = {
      date: compDate.toISOString().split("T")[0]!,
    };
    for (const metric of metrics) {
      const currentVal = point[metric];
      if (typeof currentVal === "number") {
        // Deterministic ±20% variation using a per-metric seed.
        const variation = 0.8 + (seededValue(stringSeed(metric) + 997, i, 40, 0) / 100);
        compPoint[metric] = Math.round(currentVal * variation);
      }
    }
    return compPoint;
  });

  return { points, comparisonPoints };
}

// ── Categorical Mock Data ──────────────────────────────────────────

export function generateMockCategoricalData(config: Record<string, unknown>): CategoricalData {
  const groupBy = (config.groupBy as string) ?? "channel";
  const keys = metricKeysFromConfig(config);
  const metrics = keys.length > 0 ? keys : ["sessions"];
  const comparisonEnabled = config.comparisonEnabled as boolean;

  const groups = ["Organic Search", "Direct", "Social", "Referral", "Email"];
  const categories: CategoricalData["categories"] = groups.map((group, gi) => {
    const item: Record<string, string | number> = { [groupBy]: group };
    for (let m = 0; m < metrics.length; m++) {
      // Per-metric seed from the name so each series' bars differ (was a length-
      // based seed + tiny m·31 offset → near-identical bars across metrics).
      item[metrics[m]!] = seededSeriesValue(stringSeed(metrics[m]!), gi, 4000, 800 + m * 600);
    }
    return item;
  });

  if (!comparisonEnabled) {
    return { categories };
  }

  // Generate comparison data: ±20% variation
  const comparisonCategories: CategoricalData["categories"] = categories.map((cat, gi) => {
    const compItem: Record<string, string | number> = { [groupBy]: cat[groupBy]! };
    for (const metric of metrics) {
      const currentVal = cat[metric];
      if (typeof currentVal === "number") {
        const variation = 0.8 + (seededValue(stringSeed(metric) + 997, gi, 40, 0) / 100);
        compItem[metric] = Math.round(currentVal * variation);
      }
    }
    return compItem;
  });

  return { categories, comparisonCategories };
}

// ── Distribution Mock Data ─────────────────────────────────────────

export function generateMockDistributionData(_config: Record<string, unknown>): DistributionData {
  return {
    slices: [
      { name: "Organic Search", value: 4520 },
      { name: "Direct", value: 2310 },
      { name: "Social", value: 1890 },
      { name: "Referral", value: 980 },
      { name: "Email", value: 650 },
    ],
  };
}

// ── Tabular Mock Data ──────────────────────────────────────────────

export function generateMockTabularData(config: Record<string, unknown>): TabularData {
  const columns = (config.columns as string[]) ?? ["page", "sessions"];
  const limit = (config.limit as number) ?? 10;

  const pages = [
    "/home", "/about", "/pricing", "/blog/seo-tips", "/contact",
    "/features", "/blog/analytics-guide", "/signup", "/dashboard", "/reports",
    "/integrations", "/blog/social-media", "/api-docs", "/changelog", "/support",
  ];

  const seed = columns.join("").length * 7919;
  const rows: TabularData["rows"] = pages.slice(0, Math.max(limit, 5)).map((page, ri) => {
    const row: Record<string, string | number> = {};
    for (let c = 0; c < columns.length; c++) {
      const col = columns[c]!;
      if (col === "page" || col === "url" || col === "query") {
        row[col] = page;
      } else {
        row[col] = seededValue(seed + c * 31, ri, 10000, 100);
      }
    }
    return row;
  });

  return { rows, totalRows: 150 };
}

// ── Top Posts Mock Data (Story 5.15, Task 2) ────────────────────────

const MOCK_CAPTIONS: string[] = [
  "Excited to share our latest case study — 3x ROI increase for our client in just 60 days using targeted social ads",
  "Behind the scenes at our agency: here's how we plan and execute a content strategy from scratch",
  "New blog post: 10 SEO mistakes that are costing your business leads every single day",
  "Thrilled to announce our partnership with a leading analytics platform — better insights for our clients",
  "Our latest campaign drove 500+ qualified leads in under two weeks. Here's the strategy breakdown",
  "Client spotlight: from 200 to 2,000 monthly website visitors in 90 days with our organic strategy",
  "The ultimate guide to social media analytics for agencies — free download link in bio",
  "We just hit 10,000 followers! Thank you for being part of our marketing community",
  "Five proven tactics to improve your email open rates — tested across 50+ client campaigns",
  "Join our free webinar this Thursday: mastering paid social for e-commerce brands in 2026",
];

const MOCK_PLATFORMS: TopPostRecord["platform"][] = [
  "facebook-pages", "facebook-pages", "facebook-pages",
  "linkedin-pages", "linkedin-pages", "linkedin-pages",
  "facebook-pages", "facebook-pages",
  "instagram-business", "instagram-business",
];

const MOCK_POST_TYPES: TopPostRecord["postType"][] = [
  "image", "video", "link", "image", "text", "video",
  "carousel", "image", "reel", "image",
];

export function generateMockTopPostsData(config: Record<string, unknown>): TopPostsData {
  const postCount = (config.postCount as number) ?? 5;
  const seed = 5150; // Deterministic seed for Story 5.15

  const posts: TopPostRecord[] = [];
  for (let i = 0; i < 10; i++) {
    const platform = MOCK_PLATFORMS[i]!;
    const hasThumbnail = i < 6; // First 6 have thumbnails, last 2 text-only, last 2 different platforms
    const caption = MOCK_CAPTIONS[i]!;
    const isInstagram = platform === "instagram-business";

    // Engagement decreases by rank to demonstrate visible ranking
    const engagementMultiplier = 1 - (i * 0.08);
    const baseReactions = Math.round(seededValue(seed, i, 500, 100) * engagementMultiplier);
    const baseComments = Math.round(seededValue(seed + 1, i, 100, 10) * engagementMultiplier);
    const baseShares = isInstagram ? null : Math.round(seededValue(seed + 2, i, 80, 5) * engagementMultiplier);
    const baseSaves = isInstagram ? Math.round(seededValue(seed + 3, i, 60, 5) * engagementMultiplier) : null;
    const impressions = Math.round(seededValue(seed + 4, i, 10000, 2000) * engagementMultiplier);
    const clicks = Math.round(seededValue(seed + 5, i, 200, 20) * engagementMultiplier);

    const engagementTotal = isInstagram
      ? baseReactions + baseComments + (baseSaves ?? 0)
      : baseReactions + baseComments + (baseShares ?? 0);

    posts.push({
      postId: `mock-${platform}-${i + 1}`,
      platform,
      postType: MOCK_POST_TYPES[i]!,
      content: caption.length > 0 ? caption : "[Image post]",
      createdAt: new Date(2026, 1, 28 - i).toISOString(),
      thumbnailUrl: hasThumbnail ? `https://picsum.photos/seed/post${i + 1}/300/300` : null,
      thumbnailBase64: null, // Web renders via thumbnailUrl directly
      permalinkUrl: platform === "facebook-pages"
        ? `https://www.facebook.com/mockpage/posts/${100 + i}`
        : platform === "linkedin-pages"
          ? `https://www.linkedin.com/feed/update/urn:li:activity:${7000 + i}`
          : `https://www.instagram.com/p/mock${i + 1}/`,
      reactions: baseReactions,
      comments: baseComments,
      shares: baseShares,
      saves: baseSaves,
      impressions,
      clicks,
      engagementTotal,
    });
  }

  // Return only the requested number of posts
  return { posts: posts.slice(0, Math.min(postCount, posts.length)) };
}

// ── Cover Page Mock Data (Story 5.16) ────────────────────────────────

export function generateMockCoverPageData(): null {
  // Cover page has no integration data — it's purely config-driven.
  // Widget config provides title, subtitle, logos, preparedBy, backgroundStyle.
  // reportingPeriod and clientName are derived at render time.
  return null;
}

// ── Funnel Mock Data (Story 5.18) ──────────────────────────────────

export function generateMockFunnelData(config: Record<string, unknown>): FunnelData {
  const comparisonEnabled = config.comparisonEnabled as boolean;

  const stages = [
    { label: "Sessions", value: 10000 },
    { label: "Engaged Sessions", value: 4200 },
    { label: "Form Starts", value: 890 },
    { label: "Conversions", value: 245 },
  ];

  if (!comparisonEnabled) {
    return { stages };
  }

  const comparisonStages = [
    { label: "Sessions", value: 8000 },
    { label: "Engaged Sessions", value: 3500 },
    { label: "Form Starts", value: 700 },
    { label: "Conversions", value: 200 },
  ];

  return { stages, comparisonStages };
}

// ── Demographic Mock Data (Story 5.19) ──────────────────────────────

export function generateMockDemographicData(config: Record<string, unknown>): DemographicData {
  const mode = (config.mode as string) || "device-breakdown";
  switch (mode) {
    case "device-breakdown":
      return {
        mode: "device-breakdown",
        totalSessions: 12450,
        slices: [
          { name: "Mobile", value: 7470, percentage: 60, secondaryMetrics: { avgSessionDuration: 142 } },
          { name: "Desktop", value: 3735, percentage: 30, secondaryMetrics: { avgSessionDuration: 245 } },
          { name: "Tablet", value: 1245, percentage: 10, secondaryMetrics: { avgSessionDuration: 198 } },
        ],
      };
    case "new-vs-returning":
      return {
        mode: "new-vs-returning",
        totalSessions: 12450,
        slices: [
          { name: "New Visitors", value: 8715, percentage: 70, secondaryMetrics: { avgSessionDuration: 98, pagesPerSession: 2.1 } },
          { name: "Returning Visitors", value: 3735, percentage: 30, secondaryMetrics: { avgSessionDuration: 312, pagesPerSession: 4.8 } },
        ],
      };
    case "geographic-regions":
      return {
        mode: "geographic-regions",
        totalSessions: 12450,
        slices: [
          { name: "United States", value: 4980, percentage: 40 },
          { name: "United Kingdom", value: 1868, percentage: 15 },
          { name: "Germany", value: 1245, percentage: 10 },
          { name: "Canada", value: 996, percentage: 8 },
          { name: "France", value: 871, percentage: 7 },
          { name: "Australia", value: 622, percentage: 5 },
          { name: "India", value: 498, percentage: 4 },
          { name: "Netherlands", value: 373, percentage: 3 },
          { name: "Brazil", value: 249, percentage: 2 },
          { name: "Japan", value: 187, percentage: 1.5 },
        ],
      };
    case "age-gender":
      return {
        mode: "age-gender",
        totalSessions: 12450,
        categories: [
          { bracket: "18-24", segments: [{ name: "male", value: 820 }, { name: "female", value: 690 }, { name: "other", value: 45 }] },
          { bracket: "25-34", segments: [{ name: "male", value: 1450 }, { name: "female", value: 1280 }, { name: "other", value: 65 }] },
          { bracket: "35-44", segments: [{ name: "male", value: 1180 }, { name: "female", value: 1050 }, { name: "other", value: 40 }] },
          { bracket: "45-54", segments: [{ name: "male", value: 980 }, { name: "female", value: 870 }, { name: "other", value: 30 }] },
          { bracket: "55-64", segments: [{ name: "male", value: 620 }, { name: "female", value: 540 }, { name: "other", value: 20 }] },
          { bracket: "65+", segments: [{ name: "male", value: 380 }, { name: "female", value: 320 }, { name: "other", value: 15 }] },
        ],
        disclaimer: "Based on Google signals data; may not represent all users",
      };
    default:
      return {
        mode: "device-breakdown",
        totalSessions: 12450,
        slices: [
          { name: "Mobile", value: 7470, percentage: 60 },
          { name: "Desktop", value: 3735, percentage: 30 },
          { name: "Tablet", value: 1245, percentage: 10 },
        ],
      };
  }
}

// ── Main Generator ─────────────────────────────────────────────────

export function generateMockDataForWidget(widget: WidgetConfig, comparisonEnabled?: boolean): WidgetData {
  const config = widget.config as Record<string, unknown>;
  // Global comparisonEnabled overrides per-widget config when provided (Story 5.9)
  const effectiveConfig = comparisonEnabled !== undefined
    ? { ...config, comparisonEnabled }
    : config;

  switch (widget.type) {
    case "kpi":
      return createDataEnvelope<AnyWidgetPayload>(generateMockKpiData(effectiveConfig));
    case "line-chart":
      return createDataEnvelope<AnyWidgetPayload>(generateMockTimeSeriesData(effectiveConfig));
    case "bar-chart":
      return createDataEnvelope<AnyWidgetPayload>(generateMockCategoricalData(effectiveConfig));
    case "pie-chart":
      return createDataEnvelope<AnyWidgetPayload>(generateMockDistributionData(effectiveConfig));
    case "data-table":
      return createDataEnvelope<AnyWidgetPayload>(generateMockTabularData(effectiveConfig));
    case "top-posts":
      return createDataEnvelope<AnyWidgetPayload>(generateMockTopPostsData(effectiveConfig));
    case "funnel":
      return createDataEnvelope<AnyWidgetPayload>(generateMockFunnelData(effectiveConfig));
    case "demographic":
      return createDataEnvelope<AnyWidgetPayload>(generateMockDemographicData(effectiveConfig));
    case "gauge":
      // Story 37.7 AC #8 (correction #9): gauge is NOT a platform-picker widget
      // (it is not even a `WidgetType`) — it arrives ONLY via presets
      // (e.g. ahrefs-domain-rating-gauge). It consumes the same `KpiData`
      // envelope a KPI does (GaugeWidget reads `WidgetDataEnvelope<KpiData>`,
      // routed via `transformKpi` per 37.1), so a preset-borne gauge rendered
      // through WidgetPreview/canvas no longer falls into the null `default`
      // arm and renders populated. Additive only — no palette/allowlist change.
      return createDataEnvelope<AnyWidgetPayload>(generateMockKpiData(effectiveConfig));
    case "custom-metric": {
      // Story 37.7 review #7: custom-metric consumes the `KpiData` envelope
      // (CustomMetricWidget reads `data.data` via `isKpiData`). Provide a sample
      // envelope ONLY when a formula is configured, so the preview card (which
      // seeds a sample formula) renders a populated KPI-style card, while a
      // freshly-dropped UNCONFIGURED custom-metric still gets `data: null` and
      // keeps showing its "Configure formula" edit-mode placeholder (review
      // R2-2: do not fabricate a value on the live canvas for an empty formula).
      const hasFormula = typeof effectiveConfig.formulaExpression === "string"
        && effectiveConfig.formulaExpression.trim().length > 0;
      return hasFormula
        ? createDataEnvelope<AnyWidgetPayload>(generateMockKpiData(effectiveConfig))
        : { loading: false, error: null, data: null };
    }
    case "text-block":
    case "cover-page":
      return { loading: false, error: null, data: null };
    default:
      return { loading: false, error: null, data: null };
  }
}

/**
 * Generates mock data map for all widgets in a layout.
 * Returns a Record keyed by widget ID.
 * @param comparisonEnabled - Global override from dateRange state (Story 5.9).
 *   When provided, overrides per-widget `config.comparisonEnabled`.
 */
export function generateMockData(widgets: WidgetConfig[], comparisonEnabled?: boolean): Record<string, WidgetData> {
  const dataMap: Record<string, WidgetData> = {};
  for (const widget of widgets) {
    dataMap[widget.id] = generateMockDataForWidget(widget, comparisonEnabled);
  }
  return dataMap;
}
